customElements.get('product-form') || customElements.define('product-form', class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton.querySelector('span');
    if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    this.hideErrors = this.dataset.hideErrors === 'true';
  }

  async onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();
    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');
    this.querySelector('.loading__spinner').classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData(this.form);

    try {
      const configuration = await this.getConfiguration(formData);
      const approved = this.duplicateApproved === true;
      this.duplicateApproved = false;
      const existing = approved ? null : await this.getExistingVariantSummary(configuration, formData);

      if (existing) {
        this.showQuantityDialog(existing, () => this.continueApprovedAdd());
        return;
      }

      formData.append('properties[_giftiliya_configuration]', configuration.fingerprint);

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      const response = await fetch(`${routes.cart_add_url}`, { ...config, body: formData }).then((result) => result.json());

      if (response.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: response.errors || response.description,
          message: response.message,
        });
        this.handleErrorMessage(response.description);
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        if (!soldOutMessage) return;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        this.error = true;
        return;
      }

      if (!this.cart) {
        window.location = window.routes.cart_url;
        return;
      }

      const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
      if (!this.error) {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          cartData: response,
        });
        CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
      }
      this.error = false;
      const quickAddModal = this.closest('quick-add-modal');
      if (quickAddModal) {
        document.body.addEventListener('modalClosed', () => {
          setTimeout(() => {
            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });
          });
        }, { once: true });
        quickAddModal.hide(true);
      } else {
        CartPerformance.measure('add:paint-updated-sections', () => {
          this.cart.renderContents(response);
        });
      }
    } catch (error) {
      console.error(error);
      this.handleErrorMessage('We could not add this item. Please try again.');
    } finally {
      this.submitButton.classList.remove('loading');
      if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      this.querySelector('.loading__spinner').classList.add('hidden');
      CartPerformance.measureFromEvent('add:user-action', evt);
    }
  }

  async getConfiguration(formData) {
    const properties = [];
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || match[1].startsWith('_')) continue;
      if (['preview', 'preview sample'].includes(match[1].toLowerCase())) continue;
      if (value instanceof File) {
        if (value.size > 0) properties.push([match[1], { file: value.name, size: value.size, modified: value.lastModified }]);
      } else if (value !== '') {
        properties.push([match[1], String(value).replace(/\r\n/g, '\n')]);
      }
    }
    properties.sort(([left], [right]) => left.localeCompare(right));
    const fingerprint = await this.createConfigurationFingerprint(String(formData.get('id')), properties);
    return { variantId: String(formData.get('id')), properties, fingerprint };
  }

  async createConfigurationFingerprint(variantId, properties) {
    const source = JSON.stringify({ variantId: String(variantId), properties });
    const data = new TextEncoder().encode(source);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  getPublicCartProperties(properties = {}) {
    return Object.entries(properties)
      .filter(([key, value]) => key && !key.startsWith('_') && value !== null && value !== '' && !['preview', 'preview sample'].includes(key.toLowerCase()))
      .map(([key, value]) => [key, String(value).replace(/\r\n/g, '\n')])
      .sort(([left], [right]) => left.localeCompare(right));
  }

  isImageValue(value, key = '') {
    if (typeof value !== 'string') return false;
    const imageKey = /(preview|image|photo|upload|mockup|custom)/i.test(key);
    const imageUrl = /^(https?:)?\/\//i.test(value) || value.startsWith('/');
    return imageUrl && (imageKey || /\.(avif|gif|jpe?g|png|webp)(?:[?#]|$)/i.test(value));
  }

  normalizeImageUrl(value) {
    if (!value) return '';
    try {
      return new URL(value, window.location.origin).href;
    } catch (error) {
      return '';
    }
  }

  getCartItemImage(item) {
    const customImage = Object.entries(item.properties || {}).find(([key, value]) => this.isImageValue(value, key));
    return this.normalizeImageUrl(customImage?.[1] || item.featured_image?.url || item.image);
  }

  getCurrentConfigurationImage(formData, fallbackImage) {
    for (const [key, value] of formData.entries()) {
      if (!key.startsWith('properties[')) continue;
      if (value instanceof File && value.size > 0 && value.type.startsWith('image/')) {
        if (this.activePreviewObjectUrl) URL.revokeObjectURL(this.activePreviewObjectUrl);
        this.activePreviewObjectUrl = URL.createObjectURL(value);
        return this.activePreviewObjectUrl;
      }
      if (this.isImageValue(value, key)) return this.normalizeImageUrl(value);
    }

    const scope = this.closest('.product__info-container, .product, section') || this.parentElement;
    const previewImages = Array.from(scope?.querySelectorAll('img') || []).filter((image) => {
      const context = `${image.id} ${image.className} ${image.alt} ${image.closest('[id], [class]')?.id || ''} ${image.closest('[id], [class]')?.className || ''}`;
      const box = image.getBoundingClientRect();
      return /(zepto|preview|personal|custom|mockup)/i.test(context) && box.width > 40 && box.height > 40 && image.currentSrc;
    });
    return previewImages.at(-1)?.currentSrc || fallbackImage;
  }

  getConfigurationDetails(properties) {
    return properties
      .map(([key, value]) => {
        if (this.isImageValue(value, key)) return [key, 'Photo added'];
        if (value && typeof value === 'object') {
          const filename = typeof value.file === 'string' ? value.file : value.name;
          return [key, typeof filename === 'string' && filename.trim()
            ? 'File selected: ' + filename
            : 'Details added'];
        }
        return [key, value == null ? '' : String(value)];
      })
      .filter(([, value]) => value.trim())
      .slice(0, 3);
  }

  renderConfigurationDetails(container, properties) {
    container.replaceChildren();
    const details = this.getConfigurationDetails(properties);

    if (!details.length) {
      const fallback = document.createElement('span');
      fallback.className = 'giftiliya-duplicate-cart-dialog__details-fallback';
      fallback.textContent = 'No personalization details to display';
      container.appendChild(fallback);
      return;
    }

    details.forEach(([key, value]) => {
      const detail = document.createElement('span');
      detail.className = 'giftiliya-duplicate-cart-dialog__configuration';

      const label = document.createElement('span');
      label.className = 'giftiliya-duplicate-cart-dialog__configuration-label';
      label.textContent = `${key}:`;

      const highlightedValue = document.createElement('strong');
      highlightedValue.className = 'giftiliya-duplicate-cart-dialog__configuration-value';
      highlightedValue.textContent = value;

      detail.append(label, highlightedValue);
      container.appendChild(detail);
    });
  }

  async getExistingVariantSummary(configuration, formData) {
    const cartResponse = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('Cart lookup failed');
    const cart = await cartResponse.json();

    const matchingItems = cart.items.filter((item) => String(item.variant_id) === String(configuration.variantId));
    if (!matchingItems.length) return null;

    let exactItem = matchingItems.find((item) => item.properties?._giftiliya_configuration === configuration.fingerprint);
    if (!exactItem) {
      for (const item of matchingItems) {
        const itemProperties = this.getPublicCartProperties(item.properties);
        const itemFingerprint = await this.createConfigurationFingerprint(configuration.variantId, itemProperties);
        if (itemFingerprint === configuration.fingerprint) {
          exactItem = item;
          break;
        }
      }
    }

    const displayItem = exactItem || matchingItems[0];
    const existingProperties = this.getPublicCartProperties(displayItem.properties);
    const productImage = this.normalizeImageUrl(displayItem.featured_image?.url || displayItem.image);

    return {
      currency: cart.currency || 'INR',
      productTitle: displayItem.product_title || displayItem.title || 'This item',
      quantity: matchingItems.reduce((total, item) => total + item.quantity, 0),
      unitPrice: displayItem.final_price,
      sameConfiguration: Boolean(exactItem),
      existingImage: this.getCartItemImage(displayItem) || productImage,
      currentImage: this.getCurrentConfigurationImage(formData, productImage),
      existingProperties,
      currentProperties: configuration.properties,
    };
  }

  formatMoney(amount, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 100 === 0 ? 0 : 2,
    }).format(amount / 100);
  }

  continueApprovedAdd() {
    this.duplicateApproved = true;
    this.submitButton.dataset.giftiliyaGuardBypass = 'true';
    this.submitButton.click();
    delete this.submitButton.dataset.giftiliyaGuardBypass;
    setTimeout(() => { this.duplicateApproved = false; }, 0);
  }

  showQuantityDialog(item, onAddAnother) {
    let dialog = document.getElementById('GiftiliyaDuplicateCartDialog');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'GiftiliyaDuplicateCartDialog';
      dialog.className = 'giftiliya-duplicate-cart-dialog';
      dialog.setAttribute('aria-labelledby', 'GiftiliyaDuplicateCartTitle');
      dialog.setAttribute('aria-describedby', 'GiftiliyaDuplicateCartDescription');
      dialog.innerHTML = `<form method="dialog" class="giftiliya-duplicate-cart-dialog__content">
        <button class="giftiliya-duplicate-cart-dialog__close" value="dismiss" aria-label="Close">&times;</button>
        <p class="giftiliya-duplicate-cart-dialog__quantity"></p>
        <h2 class="giftiliya-duplicate-cart-dialog__title" id="GiftiliyaDuplicateCartTitle"></h2>
        <p class="giftiliya-duplicate-cart-dialog__text" id="GiftiliyaDuplicateCartDescription"></p>
        <div class="giftiliya-duplicate-cart-dialog__comparison">
          <div class="giftiliya-duplicate-cart-dialog__card giftiliya-duplicate-cart-dialog__card--existing">
            <div class="giftiliya-duplicate-cart-dialog__card-heading">
              <span class="giftiliya-duplicate-cart-dialog__badge giftiliya-duplicate-cart-dialog__existing-badge"></span>
              <span class="giftiliya-duplicate-cart-dialog__label">In your cart</span>
            </div>
            <button class="giftiliya-duplicate-cart-dialog__image-button giftiliya-duplicate-cart-dialog__existing-image-button" type="button" aria-label="Enlarge the customization already in your cart">
              <img class="giftiliya-duplicate-cart-dialog__image giftiliya-duplicate-cart-dialog__existing-image" alt="Customization already in your cart">
            </button>
            <strong class="giftiliya-duplicate-cart-dialog__piece-count giftiliya-duplicate-cart-dialog__existing-count"></strong>
            <p class="giftiliya-duplicate-cart-dialog__details giftiliya-duplicate-cart-dialog__existing-details"></p>
          </div>
          <div class="giftiliya-duplicate-cart-dialog__connector" aria-hidden="true">
            <span class="giftiliya-duplicate-cart-dialog__connector-arrow">→</span>
            <span class="giftiliya-duplicate-cart-dialog__connector-text"></span>
          </div>
          <div class="giftiliya-duplicate-cart-dialog__card giftiliya-duplicate-cart-dialog__card--new">
            <div class="giftiliya-duplicate-cart-dialog__card-heading">
              <span class="giftiliya-duplicate-cart-dialog__badge giftiliya-duplicate-cart-dialog__next-badge"></span>
              <span class="giftiliya-duplicate-cart-dialog__label giftiliya-duplicate-cart-dialog__label--new">After adding</span>
            </div>
            <button class="giftiliya-duplicate-cart-dialog__image-button giftiliya-duplicate-cart-dialog__current-image-button" type="button" aria-label="Enlarge the customization you are adding">
              <img class="giftiliya-duplicate-cart-dialog__image giftiliya-duplicate-cart-dialog__current-image" alt="Customization you are adding">
            </button>
            <strong class="giftiliya-duplicate-cart-dialog__piece-count giftiliya-duplicate-cart-dialog__next-count"></strong>
            <p class="giftiliya-duplicate-cart-dialog__details giftiliya-duplicate-cart-dialog__current-details"></p>
          </div>
        </div>
        <div class="giftiliya-duplicate-cart-dialog__price">
          <span class="giftiliya-duplicate-cart-dialog__price-icon" aria-hidden="true">₹</span>
          <span class="giftiliya-duplicate-cart-dialog__price-label">Product total:</span>
          <strong class="giftiliya-duplicate-cart-dialog__current-total"></strong>
          <span aria-hidden="true">→</span>
          <strong class="giftiliya-duplicate-cart-dialog__next-total"></strong>
        </div>
        <div class="giftiliya-duplicate-cart-dialog__actions">
          <button class="button giftiliya-duplicate-cart-dialog__keep" value="keep" type="submit"></button>
          <button class="button button--secondary giftiliya-duplicate-cart-dialog__add" value="add" type="submit"></button>
        </div>
        <div class="giftiliya-duplicate-cart-dialog__zoom" hidden>
          <button class="giftiliya-duplicate-cart-dialog__zoom-close" type="button" aria-label="Close enlarged image">&times;</button>
          <img class="giftiliya-duplicate-cart-dialog__zoom-image" alt="Enlarged customization preview">
        </div>
      </form>`;
      const style = document.createElement('style');
      style.textContent = `.giftiliya-duplicate-cart-dialog{width:min(95vw,68rem);max-width:68rem;max-height:min(94vh,82rem);padding:0;border:0;border-radius:2rem;box-shadow:0 2.4rem 8rem rgba(0,0,0,.3);color:rgb(var(--color-foreground));background:rgb(var(--color-background));overflow:auto}.giftiliya-duplicate-cart-dialog::backdrop{background:rgba(17,17,17,.62);backdrop-filter:blur(4px)}.giftiliya-duplicate-cart-dialog__content{position:relative;padding:2.6rem 2.8rem 2.8rem;text-align:center}.giftiliya-duplicate-cart-dialog__close{position:absolute;z-index:2;top:1rem;right:1rem;width:4.2rem;height:4.2rem;border:0;border-radius:50%;background:rgba(var(--color-foreground),.07);color:currentColor;font-size:2.8rem;line-height:1;cursor:pointer}.giftiliya-duplicate-cart-dialog__quantity{display:inline-flex;margin:0 4.5rem 1rem;padding:.7rem 1.3rem;border-radius:99rem;background:#fff1f1;color:#d9343b;font-size:1.25rem;font-weight:800;letter-spacing:.04em;text-transform:uppercase}.giftiliya-duplicate-cart-dialog__title{margin:0 2rem .7rem;font-size:2.7rem;line-height:1.14;font-weight:850}.giftiliya-duplicate-cart-dialog__text{margin:0 auto 1.6rem;max-width:52rem;font-size:1.5rem;line-height:1.4}.giftiliya-duplicate-cart-dialog__comparison{display:grid;grid-template-columns:minmax(0,1fr) 6.8rem minmax(0,1fr);align-items:center;gap:1rem;margin:0 0 1.5rem}.giftiliya-duplicate-cart-dialog__card{min-width:0;padding:1.1rem;border:.1rem solid rgba(var(--color-foreground),.09);border-radius:1.5rem}.giftiliya-duplicate-cart-dialog__card--existing{background:linear-gradient(145deg,#fff4f4,#fff)}.giftiliya-duplicate-cart-dialog__card--new{background:linear-gradient(145deg,#fffbea,#fff)}.giftiliya-duplicate-cart-dialog__card-heading{display:flex;align-items:center;gap:.7rem;margin:0 0 .8rem;text-align:left}.giftiliya-duplicate-cart-dialog__badge{display:grid;flex:0 0 auto;place-items:center;width:3.2rem;height:3.2rem;border-radius:50%;background:#e63946;color:#fff;font-size:1.45rem;font-weight:850}.giftiliya-duplicate-cart-dialog__next-badge{background:#eba900;color:#111}.giftiliya-duplicate-cart-dialog__label{font-size:1.2rem;font-weight:800;color:#c52e38}.giftiliya-duplicate-cart-dialog__label--new{color:#b87900}.giftiliya-duplicate-cart-dialog__image-button{display:block;width:100%;padding:0;border:0;border-radius:1rem;background:#f5f5f5;cursor:zoom-in;overflow:hidden}.giftiliya-duplicate-cart-dialog__image{display:block;width:100%;aspect-ratio:1.16;object-fit:cover}.giftiliya-duplicate-cart-dialog__piece-count{display:block;margin:.8rem 0 .1rem;font-size:1.45rem}.giftiliya-duplicate-cart-dialog__details{margin:.25rem 0 0;font-size:1.12rem;line-height:1.3;overflow-wrap:anywhere}.giftiliya-duplicate-cart-dialog__connector{display:flex;flex-direction:column;align-items:center;gap:.4rem;color:#d9343b;font-weight:800}.giftiliya-duplicate-cart-dialog__connector-arrow{font-size:3.8rem;line-height:1}.giftiliya-duplicate-cart-dialog__connector-text{font-size:1.05rem;line-height:1.25}.giftiliya-duplicate-cart-dialog__price{display:flex;align-items:center;justify-content:center;gap:.75rem;margin:0 0 1.6rem;padding:1.15rem;border-radius:1.2rem;background:#eef9f0;font-size:1.45rem}.giftiliya-duplicate-cart-dialog__price-icon{display:grid;place-items:center;width:3rem;height:3rem;border-radius:.8rem;background:#16803c;color:#fff;font-weight:850}.giftiliya-duplicate-cart-dialog__next-total{color:#087b2d;font-size:1.65rem}.giftiliya-duplicate-cart-dialog__actions{display:grid;grid-template-columns:1fr 1fr;gap:1.2rem}.giftiliya-duplicate-cart-dialog__actions .button{margin:0;min-height:5.2rem;font-weight:750}.giftiliya-duplicate-cart-dialog__add{--color-button:255,212,0;--color-button-text:17,17,17;--alpha-button-background:1}.giftiliya-duplicate-cart-dialog__add:hover{--color-button:245,195,0}.giftiliya-duplicate-cart-dialog__keep{--color-button:255,255,255;--color-button-text:17,17,17;--alpha-button-background:1}.giftiliya-duplicate-cart-dialog__keep::after{box-shadow:0 0 0 .2rem #e6b800!important}.giftiliya-duplicate-cart-dialog__keep:hover{--color-button:255,249,219}.giftiliya-duplicate-cart-dialog__zoom{position:absolute;z-index:4;inset:0;display:grid;place-items:center;padding:5rem 1.5rem 1.5rem;background:rgba(17,17,17,.94)}.giftiliya-duplicate-cart-dialog__zoom[hidden]{display:none}.giftiliya-duplicate-cart-dialog__zoom-image{display:block;max-width:100%;max-height:100%;border-radius:1rem;object-fit:contain}.giftiliya-duplicate-cart-dialog__zoom-close{position:absolute;top:1.2rem;right:1.2rem;width:4rem;height:4rem;border:.1rem solid rgba(255,255,255,.45);border-radius:50%;background:#fff;color:#111;font-size:2.8rem;line-height:1;cursor:pointer}@media(max-width:600px){.giftiliya-duplicate-cart-dialog{max-height:95vh;border-radius:1.6rem}.giftiliya-duplicate-cart-dialog__content{padding:2.2rem 1.2rem 1.5rem}.giftiliya-duplicate-cart-dialog__close{width:3.8rem;height:3.8rem}.giftiliya-duplicate-cart-dialog__quantity{margin-bottom:.7rem;font-size:1.05rem}.giftiliya-duplicate-cart-dialog__title{margin-inline:2.4rem;font-size:2.05rem}.giftiliya-duplicate-cart-dialog__text{margin-bottom:1.2rem;font-size:1.28rem}.giftiliya-duplicate-cart-dialog__comparison{grid-template-columns:minmax(0,1fr) 2.4rem minmax(0,1fr);gap:.35rem}.giftiliya-duplicate-cart-dialog__card{padding:.65rem;border-radius:1rem}.giftiliya-duplicate-cart-dialog__card-heading{gap:.4rem;margin-bottom:.55rem}.giftiliya-duplicate-cart-dialog__badge{width:2.5rem;height:2.5rem;font-size:1.15rem}.giftiliya-duplicate-cart-dialog__label{font-size:.9rem;line-height:1.1}.giftiliya-duplicate-cart-dialog__image{aspect-ratio:1;}.giftiliya-duplicate-cart-dialog__piece-count{margin-top:.55rem;font-size:1.15rem}.giftiliya-duplicate-cart-dialog__details{font-size:.95rem;line-height:1.2}.giftiliya-duplicate-cart-dialog__connector-arrow{font-size:2.3rem}.giftiliya-duplicate-cart-dialog__connector-text{display:none}.giftiliya-duplicate-cart-dialog__price{flex-wrap:wrap;gap:.5rem;margin-bottom:1.2rem;padding:.85rem;font-size:1.2rem}.giftiliya-duplicate-cart-dialog__price-icon{width:2.5rem;height:2.5rem}.giftiliya-duplicate-cart-dialog__next-total{font-size:1.35rem}.giftiliya-duplicate-cart-dialog__actions{grid-template-columns:1fr;gap:.8rem}.giftiliya-duplicate-cart-dialog__actions .button{min-height:4.8rem}}`;
      style.textContent += `.giftiliya-duplicate-cart-dialog__quantity{background:#fff7d6;color:#6b5200}.giftiliya-duplicate-cart-dialog__card--existing{background:linear-gradient(145deg,#fff7d6,#fff)}.giftiliya-duplicate-cart-dialog__card--new{background:linear-gradient(145deg,#f7f7f7,#fff)}.giftiliya-duplicate-cart-dialog__badge{background:#ffd400;color:#111}.giftiliya-duplicate-cart-dialog__next-badge{background:#ededed;color:#333}.giftiliya-duplicate-cart-dialog__label{color:#6b5200}.giftiliya-duplicate-cart-dialog__label--new{color:#555}.giftiliya-duplicate-cart-dialog__connector{color:#6b5200}.giftiliya-duplicate-cart-dialog__price{background:#f5f5f5}.giftiliya-duplicate-cart-dialog__price-icon{background:#6b5200}.giftiliya-duplicate-cart-dialog__next-total{color:#333}.giftiliya-duplicate-cart-dialog__configuration{display:flex;flex-wrap:wrap;align-items:baseline;justify-content:center;gap:.3rem .5rem;margin:.35rem 0;padding:.55rem .7rem;border:.1rem solid #efd25b;border-radius:.8rem;background:#fff8cf}.giftiliya-duplicate-cart-dialog__configuration-label{color:#66520a;font-size:.95em;font-weight:650}.giftiliya-duplicate-cart-dialog__configuration-value{color:#111;font-size:1.08em;font-weight:850}.giftiliya-duplicate-cart-dialog__details-fallback{color:#666}.giftiliya-duplicate-cart-dialog__keep{--color-button:255,212,0;--color-button-text:17,17,17;--alpha-button-background:1}.giftiliya-duplicate-cart-dialog__keep::after{box-shadow:none!important}.giftiliya-duplicate-cart-dialog__keep:hover{--color-button:245,195,0}.giftiliya-duplicate-cart-dialog__add{--color-button:255,255,255;--color-button-text:17,17,17;--alpha-button-background:1}.giftiliya-duplicate-cart-dialog__add::after{box-shadow:0 0 0 .2rem #d9ae00!important}.giftiliya-duplicate-cart-dialog__add:hover{--color-button:255,249,219}`;
      document.head.appendChild(style);
      document.body.appendChild(dialog);

      const zoom = dialog.querySelector('.giftiliya-duplicate-cart-dialog__zoom');
      const zoomImage = dialog.querySelector('.giftiliya-duplicate-cart-dialog__zoom-image');
      dialog.querySelectorAll('.giftiliya-duplicate-cart-dialog__image-button').forEach((button) => {
        button.addEventListener('click', () => {
          zoomImage.src = button.querySelector('img').src;
          zoom.hidden = false;
          dialog.querySelector('.giftiliya-duplicate-cart-dialog__zoom-close').focus();
        });
      });
      dialog.querySelector('.giftiliya-duplicate-cart-dialog__zoom-close').addEventListener('click', () => {
        zoom.hidden = true;
        zoomImage.removeAttribute('src');
      });
    }
    const nextQuantity = item.quantity + 1;
    const pieceLabel = item.quantity === 1 ? 'piece' : 'pieces';
    const nextPieceLabel = nextQuantity === 1 ? 'piece' : 'pieces';
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__quantity').textContent = `${item.quantity} ${pieceLabel} already in cart`;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__title').textContent = item.sameConfiguration ? 'You already have this item' : 'You already have this product';
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__text').textContent = item.sameConfiguration
      ? 'This exact customization is already in your bag. Keep your existing gift, or add another.'
      : 'Your bag has this product with different details. Keep the existing gift, or add this version as another gift. Nothing will be replaced.';
    const existingImage = dialog.querySelector('.giftiliya-duplicate-cart-dialog__existing-image');
    const currentImage = dialog.querySelector('.giftiliya-duplicate-cart-dialog__current-image');
    existingImage.src = item.existingImage;
    currentImage.src = item.currentImage;
    existingImage.alt = `${item.productTitle} already in your cart`;
    currentImage.alt = `${item.productTitle} customization you are adding`;
    this.renderConfigurationDetails(
      dialog.querySelector('.giftiliya-duplicate-cart-dialog__existing-details'),
      item.existingProperties,
    );
    this.renderConfigurationDetails(
      dialog.querySelector('.giftiliya-duplicate-cart-dialog__current-details'),
      item.currentProperties,
    );
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__existing-badge').textContent = item.quantity;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__next-badge').textContent = nextQuantity;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__existing-count').textContent = `${item.quantity} ${pieceLabel}`;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__next-count').textContent = `${nextQuantity} ${nextPieceLabel} total`;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__connector-text').textContent = `Adding one makes ${nextQuantity}`;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__current-total').textContent = this.formatMoney(item.unitPrice * item.quantity, item.currency);
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__next-total').textContent = this.formatMoney(item.unitPrice * nextQuantity, item.currency);
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__keep').textContent = `Keep existing ${item.quantity === 1 ? 'gift' : 'gifts'} (${item.quantity})`;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__add').textContent = 'Add this as another gift';
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__zoom').hidden = true;
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__zoom-image').removeAttribute('src');
    dialog.returnValue = '';
    dialog.onclose = () => {
      if (dialog.returnValue === 'add') onAddAnother();
      if (dialog.returnValue === 'keep') window.location.assign(window.routes.cart_url);
    };
    dialog.showModal();
    dialog.querySelector('.giftiliya-duplicate-cart-dialog__keep').focus();
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
    if (errorMessage) this.errorMessage.textContent = errorMessage;
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) this.submitButtonText.textContent = text;
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('product-form button[name="add"]');
  if (!button || button.dataset.giftiliyaGuardBypass === 'true') return;
  const productForm = button.closest('product-form');
  if (!productForm || button.dataset.giftiliyaGuardBusy === 'true') return;

  event.preventDefault();
  event.stopImmediatePropagation();
  button.dataset.giftiliyaGuardBusy = 'true';
  button.setAttribute('aria-disabled', 'true');
  button.classList.add('loading');
  button.querySelector('.loading__spinner')?.classList.remove('hidden');
  let passed = false;

  try {
    const configuration = await productForm.getConfiguration(new FormData(productForm.form));
    const existing = await productForm.getExistingVariantSummary(configuration, new FormData(productForm.form));
    if (existing) {
      productForm.showQuantityDialog(existing, () => productForm.continueApprovedAdd());
      return;
    }

    passed = true;
    button.removeAttribute('aria-disabled');
    button.classList.remove('loading');
    button.querySelector('.loading__spinner')?.classList.add('hidden');
    button.dataset.giftiliyaGuardBypass = 'true';
    button.click();
    delete button.dataset.giftiliyaGuardBypass;
  } catch (error) {
    console.error(error);
    productForm.handleErrorMessage('We could not check your cart. Please try again.');
  } finally {
    delete button.dataset.giftiliyaGuardBusy;
    if (!passed) {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  }
}, true);

